import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode } from '@swimlane/ngx-datatable';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { FEATURES } from 'src/app/constants';
import { environment } from 'src/environments/environment';
import { ServiceAccount } from 'src/models';
import { TENANT_USER_ROLES, TenantUserRole } from 'src/models/users';
import { AuthService } from 'src/services/auth.service';
import { ServiceAccountsService } from 'src/services/service-accounts.service';

import { CreateServiceAccountComponent } from '../../components/create-service-account/create-service-account.component';

@Component({
  selector: 'app-admin-service-accounts',
  templateUrl: './admin-service-accounts.component.html',
  styleUrls: ['./admin-service-accounts.component.css'],
})
export class AdminServiceAccountsComponent implements OnInit {
  public searchText = '';
  public showInactive = false;
  public ColumnMode = ColumnMode;
  public isLoading = true;
  public serviceAccounts: Partial<ServiceAccount>[] = [];
  public environment = environment;
  public projectId?: string;

  featureEnabled = true;

  TENANT_USER_ROLES = TENANT_USER_ROLES;

  constructor(
    public authService: AuthService,
    private serviceAccountsService: ServiceAccountsService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private popoverCtrl: PopoverController,
    private route: ActivatedRoute,
  ) {
    this.projectId = this.route.snapshot.params['projectId'];
  }

  async ngOnInit() {
    await this.loadServiceAccounts();

    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.SERVICE_ACCOUNTS);

    this.isLoading = false;
  }

  async loadServiceAccounts() {
    const serviceAccounts = await this.serviceAccountsService.list({
      includeInactive: this.showInactive,
      limit: null,
      order: ['createdAt'],
      descending: true,
    });

    this.serviceAccounts = serviceAccounts.data;
  }

  async createServiceAccount() {
    if (!this.featureEnabled) {
      return;
    }

    const modal = await this.modalCtrl.create({
      component: CreateServiceAccountComponent,
      cssClass: 'generate-service-account-modal',
    });

    modal.present();

    modal.onDidDismiss().then(async ({ data }) => {
      if (data) {
        this.loadServiceAccounts();

        const confirm = await this.alertCtrl.create({
          header: 'Service Account Key',
          backdropDismiss: false,
          cssClass: 'service-account-token-modal',
          message: `Your new service account has been created with the following key. Save this key and put it somewhere safe. Once this window is closed you will not be able to view it again.<br/><br/><strong>${data}</strong>`,
          buttons: [
            {
              text: 'Copy to clipboard',
              handler: () => {
                navigator.clipboard.writeText(data);

                return false;
              },
            },
            {
              text: 'Close',
            },
          ],
        });

        confirm.present();
      }
    });
  }

  async disableServiceAccount(serviceAccount: Partial<ServiceAccount>) {
    const confirm = await this.alertCtrl.create({
      header: 'Disable service account',
      message: `You are about to disable service account "${serviceAccount.name}". Are you sure you want to proceed?`,
      buttons: [
        {
          text: 'Nevermind',
          handler: () => {},
        },
        {
          text: 'Confirm',
          handler: async () => {
            await this.serviceAccountsService.delete(serviceAccount.id);
            await this.loadServiceAccounts();
          },
        },
      ],
    });

    confirm.present();
  }

  async changeRole(serviceAccountId: string) {
    const options: ActionDropdownType[] = [
      this.authService.isTenantOwner
        ? {
            label: 'Owner',
            id: TENANT_USER_ROLES.OWNER,
            icon: 'shield-checkmark-outline',
          }
        : undefined,
      {
        label: 'Admin',
        id: TENANT_USER_ROLES.ADMIN,
        icon: 'shield-outline',
      },
      {
        label: 'Standard',
        id: TENANT_USER_ROLES.STANDARD,
      },
    ].filter(Boolean);
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (i) => {
          popover.dismiss();

          const role = options[i].id as TenantUserRole;

          await this.serviceAccountsService.updatePartial(serviceAccountId, { role });

          this.loadServiceAccounts();
        },
        onCloseCallback() {
          popover.dismiss();
        },
      },
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      event,
    });

    popover.present();
  }

  displayProjects(serviceAccount: Partial<ServiceAccount>) {
    return serviceAccount.projects.map((project) => project.name).join(', ');
  }
}
